'use client';

import { useState, useRef, useEffect } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useTourStore } from '@eliadmin/core/src/stores/tours-store';
import dynamic from 'next/dynamic';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoClose } from 'react-icons/io5';

const Calendar = dynamic(() =>
  import('@eliadmin/core/src/components/ui/calendar').then(module => module.Calendar),
);

export const TourFilterDates = () => {
  const { filter, setFilter } = useTourStore();
  const [activePicker, setActivePicker] = useState<'start' | 'end' | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslations();

  const handleDateChange = (field: 'date_start' | 'date_end', date?: Date) => {
    if (!date) return;
    setFilter({
      ...filter,
      [field]: date.toISOString(),
    });
    setActivePicker(null); // Close calendar after selection
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0'); // Add leading zero to day
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Add leading zero to month
    const year = String(date.getFullYear()).slice(-2); // Last two digits of the year
    return `${day}.${month}.${year}`; // Format: DD.MM.YY
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
      setActivePicker(null); // Close calendar if clicked outside
    }
  };

  useEffect(() => {
    if (activePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activePicker]);

  return (
    <div className="relative flex h-full items-center rounded-md bg-white p-2">
      {/* Calendar Icon */}
      <div className="flex-shrink-0">
        <FiCalendar className="h-5 w-5 text-primary" />
      </div>

      {/* Date Range Display */}
      <div className="ml-2 flex items-center space-x-2">
        <span
          className="flex cursor-pointer items-center gap-1 text-center text-sm text-gray-700"
          onClick={() => setActivePicker('start')}
        >
          {filter.date_start && (
            <IoClose
              className="h-4 w-4 text-destructive-foreground"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setFilter({ ...filter, date_start: undefined });
              }}
            />
          )}
          {filter.date_start ? formatDate(filter.date_start) : t('text811')}
        </span>
        <span>-</span>
        <span
          className="flex cursor-pointer items-center gap-1 text-center text-sm text-gray-700"
          onClick={() => setActivePicker('end')}
        >
          {filter.date_end ? formatDate(filter.date_end) : t('text86')}
          {filter.date_end && (
            <IoClose
              className="h-4 w-4 text-destructive-foreground"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setFilter({ ...filter, date_end: undefined });
              }}
            />
          )}
        </span>
      </div>

      {/* Calendar Popup */}
      {activePicker && (
        <div
          ref={calendarRef}
          className="absolute left-0 top-full z-50 mt-2 w-[300px] rounded-md border border-gray-300 bg-white p-4 shadow-lg"
        >
          <Calendar
            mode="single"
            selected={
              activePicker === 'start'
                ? filter.date_start
                  ? new Date(filter.date_start)
                  : undefined
                : filter.date_end
                  ? new Date(filter.date_end)
                  : undefined
            }
            onSelect={
              ((date: Date) =>
                handleDateChange(activePicker === 'start' ? 'date_start' : 'date_end', date)) as any
            }
            fromYear={2000}
            toYear={2030}
            weekStartsOn={1}
            showOutsideDays
            className="custom-calendar"
          />
        </div>
      )}
    </div>
  );
};
